import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  SignalCellular4Bar as SignalIcon,
  SignalCellularOff as SignalOffIcon,
  Cloud as CloudIcon,
  PhoneAndroid as PhoneIcon,
  PlaylistAddCheck as AutoTestIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  SkipNext as SkipIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import {
  LinearProgress,
} from "@material-ui/core";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { green, red, orange, grey, blue } from "@material-ui/core/colors";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";
import { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
  },
  loginContainer: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  loginPaper: {
    padding: theme.spacing(4),
    maxWidth: 400,
    width: "100%",
    borderRadius: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
    textAlign: "center",
  },
  paper: {
    padding: theme.spacing(3),
    width: "100%",
    maxWidth: 1200,
    borderRadius: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  statsBox: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1.5, 2.5),
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 120,
  },
  // Os tons 50–200 não mudam no tema escuro: lá viram tinta translúcida (texto herdado é branco)
  statConnected: {
    backgroundColor: isDarkTheme(theme) ? alpha(green[500], 0.2) : green[50],
    border: `1px solid ${isDarkTheme(theme) ? alpha(green[500], 0.4) : green[200]}`,
  },
  statDisconnected: {
    backgroundColor: isDarkTheme(theme) ? alpha(red[500], 0.2) : red[50],
    border: `1px solid ${isDarkTheme(theme) ? alpha(red[500], 0.4) : red[200]}`,
  },
  statTotal: {
    backgroundColor: isDarkTheme(theme) ? alpha(blue[500], 0.2) : blue[50],
    border: `1px solid ${isDarkTheme(theme) ? alpha(blue[500], 0.4) : blue[200]}`,
  },
  table: {
    "& .MuiTableCell-head": {
      fontWeight: 700,
      backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#f5f5f5",
      color: isDarkTheme(theme) ? theme.palette.text.primary : "#333",
    },
  },
  statusConnected: {
    ...tintStyle(isDarkTheme(theme), green, 100, 900),
    fontWeight: 600,
  },
  statusDisconnected: {
    ...tintStyle(isDarkTheme(theme), red, 100, 900),
    fontWeight: 600,
  },
  statusOther: {
    ...tintStyle(isDarkTheme(theme), orange, 100, 900),
    fontWeight: 600,
  },
  typeChip: {
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  typeBaileys: tintStyle(isDarkTheme(theme), blue, 100, 900),
  // #e8f5e9 / #2e7d32 são exatamente green[50] / green[800]
  typeCloud: tintStyle(isDarkTheme(theme), green),
  lockIcon: {
    fontSize: 60,
    color: "#667eea",
    marginBottom: theme.spacing(2),
  },
  successMsg: {
    padding: theme.spacing(1),
    ...tintStyle(isDarkTheme(theme), green),
    borderRadius: 8,
    border: `1px solid ${isDarkTheme(theme) ? alpha(green[500], 0.4) : green[200]}`,
    marginTop: theme.spacing(1),
    fontSize: "0.85rem",
  },
  errorMsg: {
    padding: theme.spacing(1),
    ...tintStyle(isDarkTheme(theme), red),
    borderRadius: 8,
    border: `1px solid ${isDarkTheme(theme) ? alpha(red[500], 0.4) : red[200]}`,
    marginTop: theme.spacing(1),
    fontSize: "0.85rem",
  },
  companyName: {
    fontSize: "0.75rem",
    color: isDarkTheme(theme) ? theme.palette.text.secondary : grey[600],
  },
  connNumber: {
    fontSize: "0.8rem",
    color: isDarkTheme(theme) ? theme.palette.text.secondary : grey[700],
    fontFamily: "monospace",
  },
  rowConnected: {
    "&:hover": { backgroundColor: isDarkTheme(theme) ? alpha(green[500], 0.2) : green[50] },
  },
  rowDisconnected: {
    "&:hover": { backgroundColor: isDarkTheme(theme) ? alpha(red[500], 0.2) : red[50] },
    opacity: 0.85,
  },
  lastUpdate: {
    fontSize: "0.75rem",
    color: grey[500],
  },
  autoTestBtn: {
    borderRadius: 8,
    fontWeight: 600,
    textTransform: "none",
    padding: "8px 20px",
  },
  autoTestResult: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[300]}`,
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#fafafa",
  },
  autoTestSummary: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  autoTestSummaryItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    borderRadius: 8,
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  resultSuccess: {
    borderLeft: `4px solid ${green[500]}`,
    backgroundColor: isDarkTheme(theme) ? alpha(green[500], 0.2) : green[50],
    padding: "6px 12px",
    marginBottom: 4,
    borderRadius: "0 6px 6px 0",
    fontSize: "0.82rem",
  },
  resultError: {
    borderLeft: `4px solid ${red[500]}`,
    backgroundColor: isDarkTheme(theme) ? alpha(red[500], 0.2) : red[50],
    padding: "6px 12px",
    marginBottom: 4,
    borderRadius: "0 6px 6px 0",
    fontSize: "0.82rem",
  },
  resultSkipped: {
    borderLeft: `4px solid ${grey[400]}`,
    backgroundColor: isDarkTheme(theme) ? alpha(grey[500], 0.2) : grey[100],
    padding: "6px 12px",
    marginBottom: 4,
    borderRadius: "0 6px 6px 0",
    fontSize: "0.82rem",
    opacity: 0.7,
  },
}));

const ConnectionsDiag = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);

  // Auth state
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");

  // Data state
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // Test message dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [testNumber, setTestNumber] = useState("5542998232794");
  const [testMessage, setTestMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Auto-test state
  const [autoTestDialogOpen, setAutoTestDialogOpen] = useState(false);
  const [autoTestNumber, setAutoTestNumber] = useState("5542998232794");
  const [autoTestRunning, setAutoTestRunning] = useState(false);
  const [autoTestResult, setAutoTestResult] = useState(null);

  const storedPassword = React.useRef("");

  const fetchConnections = useCallback(async (pwd) => {
    setLoading(true);
    setError("");
    try {
      const response = await openApi.get("/connections-diag", {
        headers: { "x-diag-password": pwd || storedPassword.current },
      });
      setData(response.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthenticated(false);
        setLoginError(i18n.t("connectionsDiag.errors.invalidPassword"));
      } else {
        setError(err?.response?.data?.error || i18n.t("connectionsDiag.errors.loadConnections"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    storedPassword.current = password;

    try {
      const response = await openApi.get("/connections-diag", {
        headers: { "x-diag-password": password },
      });
      setData(response.data);
      setAuthenticated(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setLoginError(i18n.t("connectionsDiag.errors.invalidPassword"));
      } else {
        setLoginError(err?.response?.data?.error || i18n.t("connectionsDiag.errors.connection"));
      }
    }
  };

  const handleOpenTestDialog = (connection) => {
    setSelectedConnection(connection);
    setTestNumber("5542998232794");
    setTestMessage("");
    setSendResult(null);
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!selectedConnection) return;
    setSending(true);
    setSendResult(null);

    try {
      const response = await openApi.post(
        `/connections-diag/${selectedConnection.id}/test-message`,
        {
          number: testNumber,
          message: testMessage || undefined,
        },
        {
          headers: { "x-diag-password": storedPassword.current },
        }
      );
      setSendResult({ success: true, data: response.data });
    } catch (err) {
      setSendResult({
        success: false,
        error: err?.response?.data?.error || i18n.t("connectionsDiag.errors.sendMessage"),
        details: err?.response?.data?.details || "",
      });
    } finally {
      setSending(false);
    }
  };

  const handleAutoTest = async () => {
    setAutoTestRunning(true);
    setAutoTestResult(null);
    try {
      const response = await openApi.post(
        "/connections-diag/auto-test",
        { number: autoTestNumber },
        { headers: { "x-diag-password": storedPassword.current }, timeout: 300000 }
      );
      setAutoTestResult({ success: true, data: response.data });
    } catch (err) {
      setAutoTestResult({
        success: false,
        error: err?.response?.data?.error || i18n.t("connectionsDiag.errors.autoTest"),
        details: err?.response?.data?.details || "",
      });
    } finally {
      setAutoTestRunning(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      const interval = setInterval(() => {
        fetchConnections();
      }, 30000); // Refresh a cada 30s
      return () => clearInterval(interval);
    }
  }, [authenticated, fetchConnections]);

  const getStatusChip = (status) => {
    if (status === "CONNECTED") {
      return (
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label={i18n.t("connectionsDiag.status.connected")}
          className={classes.statusConnected}
        />
      );
    }
    if (status === "DISCONNECTED" || status === "DESTROYED") {
      return (
        <Chip
          size="small"
          icon={<ErrorIcon />}
          label={status}
          className={classes.statusDisconnected}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={status || "N/A"}
        className={classes.statusOther}
      />
    );
  };

  const getTypeChip = (type) => {
    if (type === "cloudapi") {
      return (
        <Chip
          size="small"
          icon={<CloudIcon style={{ fontSize: 14 }} />}
          label="Cloud API"
          className={`${classes.typeChip} ${classes.typeCloud}`}
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<PhoneIcon style={{ fontSize: 14 }} />}
        label="Baileys"
        className={`${classes.typeChip} ${classes.typeBaileys}`}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      });
    } catch {
      return dateStr;
    }
  };

  // LOGIN SCREEN
  if (!authenticated) {
    return (
      <div className={classes.loginContainer}>
        <Paper className={classes.loginPaper}>
          <LockIcon className={classes.lockIcon} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 700 }}>
            {i18n.t("connectionsDiag.title")}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 24 }}
          >
            {i18n.t("connectionsDiag.login.subtitle")}
          </Typography>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("connectionsDiag.login.password")}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!loginError}
              helperText={loginError}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginTop: 16, borderRadius: 8, padding: "10px 0" }}
              disabled={!password}
            >
              {i18n.t("connectionsDiag.login.submit")}
            </Button>
          </form>
        </Paper>
      </div>
    );
  }

  // MAIN SCREEN
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <div>
            <Typography variant="h5" style={{ fontWeight: 700 }}>
              🔌 {i18n.t("connectionsDiag.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("connectionsDiag.subtitle")}
            </Typography>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="contained"
              className={classes.autoTestBtn}
              startIcon={autoTestRunning ? <CircularProgress size={18} style={{ color: "#fff" }} /> : <AutoTestIcon />}
              onClick={() => {
                setAutoTestResult(null);
                setAutoTestDialogOpen(true);
              }}
              disabled={autoTestRunning}
              style={{ backgroundColor: "#ff9800", color: "#fff" }}
            >
              {autoTestRunning ? i18n.t("connectionsDiag.buttons.testing") : i18n.t("connectionsDiag.buttons.autoTest")}
            </Button>
            <Button
              variant="outlined"
              startIcon={
                loading ? <CircularProgress size={18} /> : <RefreshIcon />
              }
              onClick={() => fetchConnections()}
              disabled={loading}
            >
              {i18n.t("common.refresh")}
            </Button>
          </div>
        </div>

        {error && (
          <Box className={classes.errorMsg} mb={2}>
            {error}
          </Box>
        )}

        {data && (
          <>
            <div className={classes.statsBox}>
              <Paper
                className={`${classes.statCard} ${classes.statTotal}`}
                elevation={0}
              >
                <SignalIcon style={{ color: blue[700] }} />
                <div>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {data.total}
                  </Typography>
                  <Typography variant="caption">{i18n.t("common.total")}</Typography>
                </div>
              </Paper>
              <Paper
                className={`${classes.statCard} ${classes.statConnected}`}
                elevation={0}
              >
                <CheckCircleIcon style={{ color: green[700] }} />
                <div>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {data.connected}
                  </Typography>
                  <Typography variant="caption">{i18n.t("connectionsDiag.stats.connected")}</Typography>
                </div>
              </Paper>
              <Paper
                className={`${classes.statCard} ${classes.statDisconnected}`}
                elevation={0}
              >
                <SignalOffIcon style={{ color: red[700] }} />
                <div>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {data.disconnected}
                  </Typography>
                  <Typography variant="caption">{i18n.t("connectionsDiag.stats.disconnected")}</Typography>
                </div>
              </Paper>
            </div>

            <TableContainer>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{i18n.t("common.name")}</TableCell>
                    <TableCell>{i18n.t("common.company")}</TableCell>
                    <TableCell>{i18n.t("common.type")}</TableCell>
                    <TableCell>{i18n.t("common.status")}</TableCell>
                    <TableCell>{i18n.t("connectionsDiag.table.session")}</TableCell>
                    <TableCell>{i18n.t("connectionsDiag.table.number")}</TableCell>
                    <TableCell>{i18n.t("connectionsDiag.table.battery")}</TableCell>
                    <TableCell>{i18n.t("connectionsDiag.table.updatedAt")}</TableCell>
                    <TableCell align="center">{i18n.t("connectionsDiag.table.action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.connections.map((conn) => (
                    <TableRow
                      key={conn.id}
                      className={
                        conn.status === "CONNECTED"
                          ? classes.rowConnected
                          : classes.rowDisconnected
                      }
                    >
                      <TableCell>{conn.id}</TableCell>
                      <TableCell>
                        <strong>{conn.name}</strong>
                        {conn.isDefault && (
                          <Chip
                            size="small"
                            label={i18n.t("connectionsDiag.table.default")}
                            style={{
                              marginLeft: 6,
                              fontSize: "0.65rem",
                              height: 18,
                            }}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={classes.companyName}>
                          [{conn.companyId}] {conn.companyName}
                        </span>
                      </TableCell>
                      <TableCell>{getTypeChip(conn.type)}</TableCell>
                      <TableCell>{getStatusChip(conn.status)}</TableCell>
                      <TableCell>
                        {conn.sessionActive ? (
                          <Chip
                            size="small"
                            label={i18n.t("connectionsDiag.session.active")}
                            style={{
                              ...tintStyle(isDark, green, 100, 800),
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label={i18n.t("connectionsDiag.session.inactive")}
                            style={{
                              ...tintStyle(isDark, grey, 200, 700),
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={classes.connNumber}>
                          {conn.connectedNumber || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {conn.battery
                          ? `${conn.battery}%${conn.plugged ? " ⚡" : ""}`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span className={classes.lastUpdate}>
                          {formatDate(conn.updatedAt)}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {conn.status === "CONNECTED" ? (
                          <Tooltip title={i18n.t("connectionsDiag.tooltips.sendTest")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenTestDialog(conn)}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={i18n.t("connectionsDiag.tooltips.notConnected")}>
                            <span>
                              <IconButton size="small" disabled>
                                <SendIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* DIALOG - Auto-teste geral */}
        <Dialog
          open={autoTestDialogOpen}
          onClose={() => !autoTestRunning && setAutoTestDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <AutoTestIcon style={{ color: "#ff9800" }} />
                {i18n.t("connectionsDiag.autoTest.title")}
              </div>
              {!autoTestRunning && (
                <IconButton size="small" onClick={() => setAutoTestDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              )}
            </div>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("connectionsDiag.autoTest.description")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {!autoTestResult && !autoTestRunning && (
              <TextField
                fullWidth
                variant="outlined"
                label={i18n.t("connectionsDiag.autoTest.numberLabel")}
                value={autoTestNumber}
                onChange={(e) => setAutoTestNumber(e.target.value)}
                margin="normal"
                helperText={i18n.t("connectionsDiag.autoTest.numberHelper")}
                placeholder="5542998232794"
              />
            )}

            {autoTestRunning && (
              <Box textAlign="center" py={4}>
                <CircularProgress size={48} style={{ color: "#ff9800" }} />
                <Typography variant="h6" style={{ marginTop: 16, fontWeight: 600 }}>
                  {i18n.t("connectionsDiag.autoTest.running")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("connectionsDiag.autoTest.runningHint")}
                </Typography>
                <LinearProgress style={{ marginTop: 16, borderRadius: 4 }} color="secondary" />
              </Box>
            )}

            {autoTestResult && !autoTestResult.success && (
              <Box className={classes.errorMsg}>
                ❌ {autoTestResult.error}
                {autoTestResult.details && <><br /><small>{autoTestResult.details}</small></>}
              </Box>
            )}

            {autoTestResult && autoTestResult.success && (
              <div className={classes.autoTestResult}>
                <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12 }}>
                  📋 {i18n.t("connectionsDiag.autoTest.report")}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                  {new Date(autoTestResult.data.timestamp).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                  {" | "} {i18n.t("connectionsDiag.numberValue", { number: autoTestResult.data.targetNumber })}
                  {" | "} {i18n.t("connectionsDiag.autoTest.totalDuration", { duration: autoTestResult.data.totalDuration })}
                </Typography>

                <div className={classes.autoTestSummary}>
                  <span className={classes.autoTestSummaryItem} style={tintStyle(isDark, blue)}>
                    <SignalIcon style={{ fontSize: 16 }} /> {i18n.t("connectionsDiag.autoTest.summary.total", { value: autoTestResult.data.totalConnections })}
                  </span>
                  <span className={classes.autoTestSummaryItem} style={tintStyle(isDark, green)}>
                    <CheckCircleIcon style={{ fontSize: 16 }} /> {i18n.t("connectionsDiag.autoTest.summary.success", { value: autoTestResult.data.success })}
                  </span>
                  <span className={classes.autoTestSummaryItem} style={tintStyle(isDark, red)}>
                    <ErrorIcon style={{ fontSize: 16 }} /> {i18n.t("connectionsDiag.autoTest.summary.errors", { value: autoTestResult.data.errors })}
                  </span>
                  <span className={classes.autoTestSummaryItem} style={tintStyle(isDark, grey, 200, 700)}>
                    <SkipIcon style={{ fontSize: 16 }} /> {i18n.t("connectionsDiag.autoTest.summary.skipped", { value: autoTestResult.data.skipped })}
                  </span>
                  <span className={classes.autoTestSummaryItem} style={tintStyle(isDark, orange)}>
                    <TimerIcon style={{ fontSize: 16 }} /> {i18n.t("connectionsDiag.autoTest.summary.tested", { value: autoTestResult.data.tested })}
                  </span>
                </div>

                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, marginTop: 8 }}>
                  {i18n.t("connectionsDiag.autoTest.details")}
                </Typography>
                <div style={{ maxHeight: 400, overflowY: "auto" }}>
                  {autoTestResult.data.results.map((r) => (
                    <div
                      key={r.whatsappId}
                      className={
                        r.testResult === "success"
                          ? classes.resultSuccess
                          : r.testResult === "error"
                          ? classes.resultError
                          : classes.resultSkipped
                      }
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong>
                            {r.testResult === "success" ? "✅" : r.testResult === "error" ? "❌" : "⏭️"}
                            {" "}
                            [{r.whatsappId}] {r.name}
                          </strong>
                          <span style={{ marginLeft: 8, fontSize: "0.75rem", color: isDark ? theme.palette.text.secondary : grey[600] }}>
                            {r.companyName} | {r.type}
                            {r.connectedNumber ? ` | ${r.connectedNumber}` : ""}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.75rem", color: grey[500] }}>
                          {r.duration ? `${r.duration}ms` : ""}
                        </div>
                      </div>
                      {r.testResult === "success" && (
                        <div style={{ fontSize: "0.75rem", color: isDark ? green[300] : green[700], marginTop: 2 }}>
                          MsgID: {r.messageId}
                        </div>
                      )}
                      {r.error && (
                        <div style={{ fontSize: "0.75rem", color: r.testResult === "error" ? (isDark ? red[300] : red[700]) : (isDark ? theme.palette.text.secondary : grey[600]), marginTop: 2 }}>
                          {r.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {!autoTestRunning && (
              <Button onClick={() => setAutoTestDialogOpen(false)} color="default">
                {i18n.t("common.close")}
              </Button>
            )}
            {!autoTestResult && !autoTestRunning && (
              <Button
                onClick={handleAutoTest}
                variant="contained"
                disabled={autoTestRunning || !autoTestNumber}
                startIcon={<AutoTestIcon />}
                style={{ backgroundColor: "#ff9800", color: "#fff" }}
              >
                {i18n.t("connectionsDiag.autoTest.start")}
              </Button>
            )}
            {autoTestResult && autoTestResult.success && !autoTestRunning && (
              <Button
                onClick={() => {
                  setAutoTestResult(null);
                }}
                variant="outlined"
                startIcon={<RefreshIcon />}
              >
                {i18n.t("connectionsDiag.autoTest.again")}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* DIALOG - Enviar mensagem de teste */}
        <Dialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {i18n.t("connectionsDiag.testDialog.title")}
            {selectedConnection && (
              <Typography variant="body2" color="textSecondary">
                {i18n.t("connectionsDiag.testDialog.connection")}: <strong>{selectedConnection.name}</strong> (ID:{" "}
                {selectedConnection.id})
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("connectionsDiag.testDialog.number")}
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              margin="normal"
              helperText={i18n.t("connectionsDiag.testDialog.numberHelper")}
              placeholder="5542998232794"
            />
            <TextField
              fullWidth
              variant="outlined"
              label={i18n.t("connectionsDiag.testDialog.message")}
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              helperText={i18n.t("connectionsDiag.testDialog.messageHelper")}
              placeholder={i18n.t("connectionsDiag.testDialog.messagePlaceholder")}
            />
            {sendResult && sendResult.success && (
              <Box className={classes.successMsg}>
                ✅ {sendResult.data.message}
                <br />
                <small>
                  ID: {sendResult.data.details?.messageId} |{" "}
                  {i18n.t("connectionsDiag.numberValue", { number: sendResult.data.details?.number })}
                </small>
              </Box>
            )}
            {sendResult && !sendResult.success && (
              <Box className={classes.errorMsg}>
                ❌ {sendResult.error}
                {sendResult.details && (
                  <>
                    <br />
                    <small>{sendResult.details}</small>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestDialogOpen(false)} color="default">
              {i18n.t("common.close")}
            </Button>
            <Button
              onClick={handleSendTest}
              color="primary"
              variant="contained"
              disabled={sending || !testNumber}
              startIcon={
                sending ? <CircularProgress size={18} /> : <SendIcon />
              }
            >
              {sending ? i18n.t("connectionsDiag.testDialog.sending") : i18n.t("connectionsDiag.testDialog.send")}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default ConnectionsDiag;
